import { createContext, useContext } from "react";
import type React from "react";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";

/**
 * PRODUCT_DEV-748: 画面の外枠（下部ナビ・メニュー・ロゴ・ホーム導線）を
 * ビルド variant ごとに差し替えるための注入口。
 *
 * Minizo は Unizo と同じページ（メモ・ToDo・設定・ガイド 等）をそのまま使う一方で、
 * 外枠だけが違う。ページは 30 本以上が `MobileLayout` を内部で描画しているため、
 * ページ側に手を入れずに外枠を差し替えられる必要がある。
 *
 * **runtime の if 分岐にしなかった理由**: `MobileLayout` が両方の実装を import すると、
 * Unizo 側のナビ・メニュー（「商談記録」「顧客一覧」「管理者画面」等のラベルを含む）が
 * Minizo の dist にも入る。Minizo は社外へ配るデモなので、バンドルから読み取れる状態を
 * 避けたい（PRODUCT_DEV-688 で別エントリ方式を選んだ理由そのもの）。
 * Provider 経由なら各エントリが自分の実装だけを import するので Rollup が落とせる。
 *
 * 既定値をあえて持たない（Provider 必須）のも同じ理由。既定値に Unizo の実装を置くと
 * このモジュールが Unizo のナビを常に引き込んでしまう。
 */

export interface ChromeTabBarProps {
  onMenuClick: () => void;
  /** Dashboard の検索オーバーレイ起動（Minizo では未使用）。 */
  onSearchClick?: () => void;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
}

export interface ChromeMenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  auth: WhoamiMe;
}

export interface Chrome {
  /** 下部グローバルナビ。 */
  TabBar: React.ComponentType<ChromeTabBarProps>;
  /** メニューシート。 */
  MenuSheet: React.ComponentType<ChromeMenuSheetProps>;
  /** 左上ロゴ（`MainHeader` が描画する）。 */
  Logo: React.ComponentType<{ className?: string }>;
  /** ロゴをタップしたときの遷移先。 */
  homePath: string;
  /** ロゴの aria-label に使うホーム画面名。 */
  homeLabel: string;
  /**
   * `/` を開いたときの画面名（Unizo=商談一覧 / Minizo=メモ）。
   *
   * PRODUCT_DEV-980: 手帳が ON のテナントは `homeLabel` だけが「手帳」に差し替わり、
   * `/` は商談一覧のまま。
   */
  topLabel: string;
  /**
   * ヘッダーの通知ベル。**未指定なら通知機能そのものを持たない**。
   *
   * PRODUCT_DEV-748: Minizo は未指定。通知カテゴリが「商談記録が登録された」
   * 「商談グループが更新された」等すべて商談由来で、Minizo には通知の発生源が
   * 1 つも無いため、ベルを置いても永久に空になる。
   *
   * boolean のフラグにせずコンポーネントを注入する形にしているのは、
   * フラグだと `MainHeader` がベルを import したままになり、30 秒間隔の
   * polling フックごと Minizo の dist に残るため（描画されないだけで消えない）。
   * 注入式なら minizoChrome が持たない＝Rollup が落とせる。
   *
   * Minizo 独自の通知イベント（文字起こし完了など）を定義するなら、そのときに足す。
   */
  NotificationBell?: React.ComponentType;
}

const ChromeContext = createContext<Chrome | null>(null);

export const ChromeProvider = ChromeContext.Provider;

export const useChrome = (): Chrome => {
  const chrome = useContext(ChromeContext);
  if (!chrome) {
    // Provider は各エントリの RootLayout が置く。抜けると下部ナビもロゴも消えた
    // 画面が黙って出てしまうため、握りつぶさず落とす。
    throw new Error(
      "ChromeProvider が見つかりません。router の RootLayout で ChromeProvider を張ってください。",
    );
  }
  return chrome;
};

/**
 * ホーム（起点画面）への戻り導線。
 *
 * PRODUCT_DEV-748: `backLabel="商談記録"` のようにトップ画面名を直書きしている箇所が
 * 各所にあるが、その名前は variant で変わる（Unizo=商談記録 / Minizo=メモ）。
 * 直書きのままだと Minizo で「商談記録へ戻る」と表示され、存在しない画面を指してしまう。
 */
export const useHomeNav = (): { to: string; label: string } => {
  const { homePath, homeLabel } = useChrome();
  return { to: homePath, label: homeLabel };
};

/**
 * PRODUCT_DEV-1043: ChromeProvider の外（画面だけを描くテスト）でも落とさない版。
 * 来た画面へ戻る ← は、記録が動いていないときは使われないので、外では null で足りる。
 */
export const useOptionalHomeNav = (): { to: string; label: string } | null => {
  const chrome = useContext(ChromeContext);
  return chrome ? { to: chrome.homePath, label: chrome.homeLabel } : null;
};
